import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { CartItem } from '../types';
import { API_BASE } from '../utils/config';
import { apiFetch } from '../utils/apiFetch';

interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  cartTotal: number;
  loading: boolean;
  addToCart: (productId: number, quantity?: number) => Promise<boolean>;
  removeFromCart: (cartItemId: string) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  refreshCart: () => Promise<void>;
  clearCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading] = useState(false);

  const refreshCart = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) { setCartItems([]); return; }
    try {
      const res = await apiFetch(`${API_BASE}/cart_item`);
      if (res.ok) {
        const data = await res.json();
        setCartItems(data.cart_items || []);
      }
    } catch {
      setCartItems([]);
    }
  }, []);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = useCallback(async (productId: number, quantity = 1): Promise<boolean> => {
    try {
      const res = await apiFetch(`${API_BASE}/cart_item`, {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, quantity }),
      });
      if (res.ok) {
        await refreshCart();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [refreshCart]);

  const removeFromCart = useCallback(async (cartItemId: string) => {
    try {
      await apiFetch(`${API_BASE}/cart_item/${cartItemId}`, { method: 'DELETE' });
      setCartItems((prev) => prev.filter((item) => item.id !== cartItemId));
    } catch {}
  }, []);

  const updateQuantity = useCallback(async (cartItemId: string, quantity: number) => {
    try {
      const res = await apiFetch(`${API_BASE}/cart_item/${cartItemId}`, {
        method: 'PUT',
        body: JSON.stringify({ quantity }),
      });
      if (res.ok) {
        setCartItems((prev) =>
          prev.map((item) => (item.id === cartItemId ? { ...item, quantity } : item))
        );
      }
    } catch {}
  }, []);

  const clearCart = useCallback(async () => {
    const snapshot = [...cartItems];
    setCartItems([]);
    await Promise.all(snapshot.map(item =>
      apiFetch(`${API_BASE}/cart_item/${item.id}`, { method: 'DELETE' }).catch(() => {})
    ));
  }, [cartItems]);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.selling_price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{ cartItems, cartCount, cartTotal, loading, addToCart, removeFromCart, updateQuantity, refreshCart, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};
